<template>
  <BRow
    v-if="!loading"
    class="mb-3"
  >
    <BCol>
      <BTable
        striped
        hover
        responsive
        :items="sortedUsers"
        :fields="tableFields"
        show-empty
        empty-text="No users found"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        @update:sort-by="sortBy = $event"
        @update:sort-desc="sortDesc = $event"
      >
        <template #cell(role)="data">
          <BBadge 
            variant="light" 
            :class="getRoleClass(data.item.role)"
          >
            {{ data.item.role }}
          </BBadge>
        </template>
        <template #cell(is_active)="data">
          <BBadge
            variant="light"
            :class="getStatusClass(data.item.is_active)"
            class="status-badge"
            :style="props.pendingToggleUserId === data.item.id ? 'opacity: 0.5; pointer-events: none;' : ''"
            role="button"
            tabindex="0"
            @click="toggleUserStatus(data.item)"
            @keydown.enter="toggleUserStatus(data.item)"
          >
            {{ getStatusLabel(data.item.is_active) }}
          </BBadge>
        </template>
        <template #cell(actions)="data">
          <BButtonGroup size="sm">
            <BButton
              variant="outline-primary"
              size="sm"
              @click="$emit('reset-password', data.item.id)"
            >
              Reset Password
            </BButton>
          </BButtonGroup>
        </template>
      </BTable>

      <!-- Pagination -->
      <BPagination
        :model-value="page"
        :total-rows="total"
        :per-page="50"
        align="center"
        class="mt-3"
        @update:model-value="$emit('update:page', Number($event))"
      />

      <!-- Results Info -->
      <BRow class="mt-2">
        <BCol class="text-muted small text-end">
          {{ userCount }}
        </BCol>
      </BRow>
    </BCol>
  </BRow>
</template>

<script setup lang="ts">
import { BRow, BCol, BTable, BBadge, BButton, BButtonGroup, BPagination } from "bootstrap-vue-next";
import type { User } from "@/composables/useUserApi";
import { computed, ref } from "vue";

interface TableField {
  key: string;
  label?: string;
  sortable?: boolean;
}

interface BTableSortBy {
  key: string;
  order: 'asc' | 'desc' | undefined;
}

const props = defineProps<{
  users: User[];
  total: number;
  page: number;
  loading: boolean;
  pendingToggleUserId?: string | null;
}>();

const emit = defineEmits<{
  "toggle-status": [user: User];
  "reset-password": [userId: string];
  "update:page": [page: number];
}>();

const sortBy = ref<BTableSortBy[] | undefined>(undefined);
const sortDesc = ref<boolean>(false);

const tableFields: TableField[] = [
  { key: "username", label: "Username", sortable: true },
  { key: "email", label: "Email", sortable: true },
  { key: "display_name", label: "Display Name", sortable: true },
  { key: "role", label: "Role", sortable: true },
  { key: "is_active", label: "Status", sortable: true },
  { key: "actions", label: "Actions", sortable: false },
];

const userCount = computed(() => `Showing ${props.users.length} of ${props.total} users`);

const sortedUsers = computed(() => {
  const sortItem = sortBy.value?.[0];
  const sortKey = sortItem?.key;
  if (!sortKey || !props.users?.length) {
    return props.users || [];
  }
  
  const isSortDesc = sortItem?.order === 'desc' ? true : false;
  
  try {
    return [...props.users].sort((a, b) => {
      if (!a || !b) return 0;
      
      const aVal = a[sortKey as keyof User];
      const bVal = b[sortKey as keyof User];
      
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return isSortDesc ? -1 : 1;
      if (bVal == null) return isSortDesc ? 1 : -1;
      
      if (typeof aVal === "string" && typeof bVal === "string") {
        return isSortDesc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
      }
      if (typeof aVal === "boolean" && typeof bVal === "boolean") {
        return isSortDesc ? Number(bVal) - Number(aVal) : Number(aVal) - Number(bVal);
      }
      return 0;
    });
  } catch {
    return props.users || [];
  }
});

const getRoleClass = (role: string) => role === "admin" ? "text-danger" : "text-dark";
const getStatusClass = (isActive: boolean) => isActive ? "text-success" : "text-warning";
const getStatusLabel = (isActive: boolean) => isActive ? "Active" : "Inactive";

const toggleUserStatus = (user: User) => {
  emit("toggle-status", user);
};
</script>
<style scoped>
:deep(.table tbody td) {
  vertical-align: middle;
}
</style>